import React from 'react';
import clsx from 'clsx';
import * as Headless from '@headlessui/react';
import {
  BoldIcon,
  ItalicIcon,
  UnderlineIcon,
  StrikethroughIcon,
  ListBulletIcon,
  NumberedListIcon,
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  PaintBrushIcon,
  Bars3BottomLeftIcon,
  Bars3BottomRightIcon,
  NoSymbolIcon,
  TableCellsIcon
} from '@heroicons/react/20/solid';
import { PhotoIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import type { Editor as EditorType } from '@tiptap/react';
import { useEditorState } from '@tiptap/react';
import { EditorLinkButton } from './editor-link-button';

// Custom center-align icon matching the style of bars-3-bottom-left
const Bars3CenterIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className={className}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3.75 6.75h16.5M7.875 12h8.25M3.75 17.25h16.5"
    />
  </svg>
);

// Custom fill bucket icon matching the style of paint brush
const FillBucketIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1}
    stroke="currentColor"
    className={className}
  >
    <path d="M20.911 14.216l-.411-.596-.411.596C19.74 14.72 18 17.3 18 18.5a2.5 2.5 0 0 0 5 0c0-1.2-1.74-3.78-2.089-4.284zM20.5 20a1.502 1.502 0 0 1-1.5-1.5 9.725 9.725 0 0 1 1.5-3.096A9.725 9.725 0 0 1 22 18.5a1.502 1.502 0 0 1-1.5 1.5zm-9-17.207L9.145 5.148a.476.476 0 0 0-.09-.023c-3.475-.17-5.962.425-6.743 1.59-.027.042-.07.077-.092.12a1.394 1.394 0 0 0 .118 1.522c.694.973 2.685 1.732 5.833 1.732a23.887 23.887 0 0 0 2.89-.192 1.494 1.494 0 1 0 .076-1.016c-4.77.618-7.418-.308-7.986-1.104-.812-1.14 3.1-1.71 5.044-1.679L6.32 7.973c.386.05.836.08 1.318.096L11.5 4.207l7.293 7.293-8.09 8.091a1.74 1.74 0 0 1-2.405 0l-4.889-4.888a1.702 1.702 0 0 1 0-2.405l1.514-1.514a9.152 9.152 0 0 1-1.101-.312l-1.12 1.12a2.703 2.703 0 0 0 0 3.818l4.889 4.888a2.7 2.7 0 0 0 3.818 0l8.798-8.798zM12 9.5a.5.5 0 1 1 .5.5.5.5 0 0 1-.5-.5z" />
  </svg>
);

// Font size options for the font size selector.
//
// Sizes are points, because these are print documents: the stylesheet a document is generated
// with is written in pt, and the renderer measures the page in pt. Writing px here made the
// number in the toolbar and the size on the page disagree by a quarter — picking 32 printed
// 24pt, and picking 16 printed 12pt, which reads as body text.
const FONT_SIZES = [
  '8',
  '9',
  '10',
  '11',
  '12',
  '14',
  '16',
  '18',
  '20',
  '24',
  '28',
  '32',
  '36',
  '48',
  '72'
] as const;

const ColorPickerButton = ({
  icon,
  text,
  color,
  onChange,
  disabled,
  title
}: {
  icon?: React.ReactNode;
  text?: string;
  color: string;
  onChange: (color: string) => void;
  disabled: boolean;
  title: string;
}) => {
  return (
    <label
      className={clsx(
        'relative p-1 rounded-md cursor-pointer flex flex-col items-center gap-[1px] w-6',
        disabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-neutral-200 dark:hover:bg-white/10'
      )}
      title={title}
    >
      {/* Icon or Text */}
      {text && (
        <span className="text-xs/3 font-bold text-neutral-700 dark:text-neutral-300">{text}</span>
      )}
      {icon && <div className="text-neutral-700 dark:text-neutral-300">{icon}</div>}

      {/* Color Indicator Bar */}
      <div className="w-full h-0.5 rounded-[1px]" style={{ backgroundColor: color }} />

      {/* Hidden Color Input */}
      <input
        type="color"
        value={color}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="absolute opacity-0 w-0 h-0"
      />
    </label>
  );
};

const FontSizeSelector = ({
  currentFontSize,
  onSetFontSize,
  onUnsetFontSize,
  disabled
}: {
  currentFontSize: string | null;
  onSetFontSize: (size: string) => void;
  onUnsetFontSize: () => void;
  disabled: boolean;
}) => {
  return (
    <Headless.Menu as="div" className="relative z-30">
      <Headless.MenuButton
        as={Headless.Button}
        disabled={disabled}
        className={clsx(
          'px-2 py-1 hover:bg-neutral-200 dark:hover:bg-white/10 rounded-md text-xs font-medium min-w-[3.5rem] text-center',
          disabled
            ? 'text-neutral-400 dark:text-neutral-500 cursor-not-allowed'
            : currentFontSize
              ? 'text-primary-500 dark:text-primary-400'
              : 'text-neutral-500 dark:text-neutral-400'
        )}
      >
        {currentFontSize ? currentFontSize.replace(/p[tx]$/, '') : 'Size'}
      </Headless.MenuButton>
      <Headless.MenuItems className="absolute left-0 top-full mt-1 w-24 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-md shadow-lg z-30 max-h-60 overflow-y-auto">
        {FONT_SIZES.map((size) => {
          const sizeValue = `${size}pt`;
          const isActive = currentFontSize === sizeValue;
          return (
            <Headless.MenuItem key={size}>
              {({ active }) => (
                <button
                  onClick={() => onSetFontSize(sizeValue)}
                  className={clsx(
                    'w-full px-3 py-1.5 text-left text-sm',
                    active && 'bg-neutral-100 dark:bg-neutral-700',
                    isActive && 'text-primary-500 dark:text-primary-400 font-semibold',
                    !isActive && 'text-neutral-700 dark:text-neutral-300'
                  )}
                >
                  {sizeValue}
                </button>
              )}
            </Headless.MenuItem>
          );
        })}
        <div className="border-t border-neutral-200 dark:border-neutral-700">
          <Headless.MenuItem>
            {({ active }) => (
              <button
                onClick={onUnsetFontSize}
                className={clsx(
                  'w-full px-3 py-1.5 text-left text-sm',
                  active && 'bg-neutral-100 dark:bg-neutral-700',
                  !currentFontSize
                    ? 'text-primary-500 dark:text-primary-400 font-semibold'
                    : 'text-neutral-700 dark:text-neutral-300'
                )}
              >
                Default
              </button>
            )}
          </Headless.MenuItem>
        </div>
      </Headless.MenuItems>
    </Headless.Menu>
  );
};

// The toolbar state for an editor that can no longer answer questions about
// itself: everything unavailable, colors at their defaults.
const DISABLED_EDITOR_STATE = {
  hasSelection: false,
  canSetTextColor: false,
  canSetHighlight: false,
  currentTextColor: '#000000',
  currentHighlightColor: '#ffffff',
  currentFontSize: null as string | null,
  canSetFontSize: false,
  isBold: false,
  canBold: false,
  isItalic: false,
  canItalic: false,
  isStrike: false,
  isUnderline: false,
  canUnderline: false,
  canStrike: false,
  isAlignLeft: false,
  isAlignCenter: false,
  isAlignRight: false,
  canSetTextAlign: false,
  canClearMarks: false,
  isLink: false,
  canSetLink: false,
  isParagraph: false,
  isHeading1: false,
  isHeading2: false,
  isHeading3: false,
  isBulletList: false,
  isOrderedList: false,
  isCodeBlock: false,
  isBlockquote: false,
  canUndo: false,
  canRedo: false,
  canInsertImagePlaceholder: false,
  isInTable: false,
  canAddRowBefore: false,
  canAddRowAfter: false,
  canAddColumnBefore: false,
  canAddColumnAfter: false,
  canDeleteRow: false,
  canDeleteColumn: false
};

/** A cluster of related buttons, which wraps as one so its divider never strands at a line end. */
const ToolbarGroup = (props: { children: React.ReactNode }) => (
  <span className="inline-flex items-center gap-0.5 border-l border-neutral-300 pl-1 ml-1 first:ml-0 first:border-0 first:pl-0 dark:border-neutral-600">
    {props.children}
  </span>
);

const TableMenu = (props: {
  disabled: boolean;
  actions: { label: string; enabled: boolean; run: () => void }[];
}) => (
  <Headless.Menu as="div" className="relative z-30">
    <Headless.MenuButton
      as={Headless.Button}
      disabled={props.disabled}
      aria-label="Table"
      className={clsx(
        'p-1 hover:bg-neutral-200 dark:hover:bg-white/10 rounded-md',
        props.disabled
          ? 'text-neutral-400 dark:text-neutral-500 cursor-not-allowed'
          : 'text-neutral-500 dark:text-neutral-400'
      )}
    >
      <TableCellsIcon className="size-4" />
    </Headless.MenuButton>
    <Headless.MenuItems className="absolute left-0 top-full mt-1 w-44 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-md shadow-lg z-30">
      {props.actions.map((action) => (
        <Headless.MenuItem key={action.label} disabled={!action.enabled}>
          {({ active }) => (
            <button
              type="button"
              onClick={action.run}
              disabled={!action.enabled}
              className={clsx(
                'w-full px-3 py-1.5 text-left text-sm',
                active && 'bg-neutral-100 dark:bg-neutral-700',
                action.enabled
                  ? 'text-neutral-700 dark:text-neutral-300'
                  : 'text-neutral-400 dark:text-neutral-500 cursor-not-allowed'
              )}
            >
              {action.label}
            </button>
          )}
        </Headless.MenuItem>
      ))}
    </Headless.MenuItems>
  </Headless.Menu>
);

const ToolbarIconButton = (props: {
  label: string;
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <Headless.Button
    onClick={props.onClick}
    disabled={props.disabled}
    aria-label={props.label}
    title={props.label}
    className={clsx(
      'p-1 hover:bg-neutral-200 dark:hover:bg-white/10 rounded-md text-sm',
      props.active
        ? 'text-primary-500 dark:text-primary-400'
        : 'text-neutral-500 dark:text-neutral-400'
    )}
  >
    {props.children}
  </Headless.Button>
);

export const MenuBar = ({
  editor,
  editable = true,
  readOnlyNote,
  showPreview,
  onPreviewToggle,
  showImageButton = false,
  subtle
}: {
  editor: EditorType;
  editable?: boolean;
  readOnlyNote?: string;
  showPreview?: boolean;
  onPreviewToggle?: () => void;
  showImageButton?: boolean;
  /**
   * Trades the filled tray for a hairline strip. A document editor is a page
   * with a toolbar over it; a mail composer is a message with formatting
   * available, and a grey bar between the header and the body says the wrong
   * one of those.
   */
  subtle?: boolean;
}) => {
  // Read the current editor's state, and re-render the component when it changes
  const editorState = useEditorState({
    editor,
    selector: (ctx) => {
      // can() throws on a destroyed editor, so degrade to a disabled toolbar
      // instead of crashing.
      if (ctx.editor.isDestroyed) {
        return DISABLED_EDITOR_STATE;
      }
      return {
        hasSelection: !ctx.editor.state.selection.empty,
        canSetTextColor: ctx.editor.can().chain().setColor('#000000').run() ?? false,
        canSetHighlight: ctx.editor.can().chain().setBackgroundColor('#ffffff').run() ?? false,
        currentTextColor:
          (ctx.editor.getAttributes('textStyle').color as string | undefined) ?? '#000000',
        currentHighlightColor: ctx.editor.storage.smartHighlight.getCurrentColor(ctx.editor),
        currentFontSize:
          (ctx.editor.getAttributes('fontSize').fontSize as string | undefined) ?? null,
        canSetFontSize: ctx.editor.can().chain().setFontSize('12pt').run() ?? false,
        isBold: ctx.editor.isActive('bold') ?? false,
        canBold: ctx.editor.can().chain().toggleBold().run() ?? false,
        isItalic: ctx.editor.isActive('italic') ?? false,
        canItalic: ctx.editor.can().chain().toggleItalic().run() ?? false,
        isStrike: ctx.editor.isActive('strike') ?? false,
        isUnderline: ctx.editor.isActive('underline') ?? false,
        canUnderline: ctx.editor.can().chain().toggleUnderline().run() ?? false,
        canStrike: ctx.editor.can().chain().toggleStrike().run() ?? false,
        isAlignLeft: ctx.editor.isActive({ textAlign: 'left' }) ?? false,
        isAlignCenter: ctx.editor.isActive({ textAlign: 'center' }) ?? false,
        isAlignRight: ctx.editor.isActive({ textAlign: 'right' }) ?? false,
        canSetTextAlign: ctx.editor.can().chain().setTextAlign('left').run() ?? false,
        canClearMarks: ctx.editor.can().chain().unsetAllMarks().run() ?? false,
        isLink: ctx.editor.isActive('link'),
        canSetLink:
          ctx.editor.isEditable && ctx.editor.can().setLink({ href: 'https://example.com' }),
        isParagraph: ctx.editor.isActive('paragraph') ?? false,
        isHeading1: ctx.editor.isActive('heading', { level: 1 }) ?? false,
        isHeading2: ctx.editor.isActive('heading', { level: 2 }) ?? false,
        isHeading3: ctx.editor.isActive('heading', { level: 3 }) ?? false,
        isBulletList: ctx.editor.isActive('bulletList') ?? false,
        isOrderedList: ctx.editor.isActive('orderedList') ?? false,
        isCodeBlock: ctx.editor.isActive('codeBlock') ?? false,
        isBlockquote: ctx.editor.isActive('blockquote') ?? false,
        canUndo: ctx.editor.can().chain().undo().run() ?? false,
        canRedo: ctx.editor.can().chain().redo().run() ?? false,
        canInsertImagePlaceholder:
          ctx.editor.can().chain().insertImagePlaceholder('').run() ?? false,
        isInTable:
          ctx.editor.isActive('table') ||
          ctx.editor.isActive('tableCell') ||
          ctx.editor.isActive('tableHeader'),
        canAddRowBefore: ctx.editor.can().chain().addRowBefore().run() ?? false,
        canAddRowAfter: ctx.editor.can().chain().addRowAfter().run() ?? false,
        canAddColumnBefore: ctx.editor.can().chain().addColumnBefore().run() ?? false,
        canAddColumnAfter: ctx.editor.can().chain().addColumnAfter().run() ?? false,
        canDeleteRow: ctx.editor.can().chain().deleteRow().run() ?? false,
        canDeleteColumn: ctx.editor.can().chain().deleteColumn().run() ?? false
      };
    }
  });

  if (!editable) {
    return (
      <div className="flex min-h-10 items-center gap-3 px-3 py-1.5 text-sm text-neutral-500 dark:text-neutral-400">
        <span>{readOnlyNote ?? 'Read-only document'}</span>
        {onPreviewToggle && (
          <button
            type="button"
            onClick={onPreviewToggle}
            className="ml-auto text-primary-600 dark:text-primary-400"
          >
            Back to pages
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      className={
        subtle
          ? 'border-t border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 py-1.5'
          : 'bg-neutral-100 dark:bg-neutral-700 rounded-t-md p-2'
      }
    >
      {/* Back to pages sits outside the wrapping group, so a narrow width wraps the formatting
          buttons and never strands it alone on a line of its own. */}
      <div className="flex items-start gap-2">
        <div className="button-group flex min-w-0 flex-1 flex-row flex-wrap items-center gap-y-1">
          <ToolbarGroup>
            <ColorPickerButton
              text="A"
              color={editorState.currentTextColor}
              onChange={(color) => editor.chain().focus().setColor(color).run()}
              disabled={!editorState.canSetTextColor}
              title="Text Color"
            />
            <ColorPickerButton
              icon={
                editorState.hasSelection ? (
                  <PaintBrushIcon className="size-3" />
                ) : (
                  <FillBucketIcon className="size-3" />
                )
              }
              color={editorState.currentHighlightColor}
              onChange={(color) => editor.chain().focus().setSmartBackgroundColor(color).run()}
              disabled={!editorState.canSetHighlight}
              title="Fill or Highlight"
            />
            <Headless.Button
              onClick={() => editor.chain().focus().clearSmartBackgroundColor().run()}
              disabled={!editorState.canSetHighlight}
              className={clsx(
                'p-1 hover:bg-neutral-200 dark:hover:bg-white/10 rounded-md',
                editorState.canSetHighlight
                  ? 'text-neutral-700 dark:text-neutral-300'
                  : 'text-neutral-400 dark:text-neutral-500'
              )}
              aria-label="Clear Fill or Highlight"
              title="Clear Fill or Highlight"
            >
              <NoSymbolIcon className="size-4" />
            </Headless.Button>
            <FontSizeSelector
              currentFontSize={editorState.currentFontSize}
              onSetFontSize={(size) => editor.chain().focus().setFontSize(size).run()}
              onUnsetFontSize={() => editor.chain().focus().unsetFontSize().run()}
              disabled={!editorState.canSetFontSize}
            />
          </ToolbarGroup>
          <ToolbarGroup>
            <ToolbarIconButton
              label="Bold"
              active={editorState.isBold}
              disabled={!editorState.canBold}
              onClick={() => editor.chain().focus().toggleBold().run()}
            >
              <BoldIcon className="size-4" />
            </ToolbarIconButton>
            <ToolbarIconButton
              label="Italic"
              active={editorState.isItalic}
              disabled={!editorState.canItalic}
              onClick={() => editor.chain().focus().toggleItalic().run()}
            >
              <ItalicIcon className="size-4" />
            </ToolbarIconButton>
            <ToolbarIconButton
              label="Underline"
              active={editorState.isUnderline}
              disabled={!editorState.canUnderline}
              onClick={() => editor.chain().focus().toggleUnderline().run()}
            >
              <UnderlineIcon className="size-4" />
            </ToolbarIconButton>
            <ToolbarIconButton
              label="Strikethrough"
              active={editorState.isStrike}
              disabled={!editorState.canStrike}
              onClick={() => editor.chain().focus().toggleStrike().run()}
            >
              <StrikethroughIcon className="size-4" />
            </ToolbarIconButton>
          </ToolbarGroup>
          <ToolbarGroup>
            <EditorLinkButton
              editor={editor}
              active={editorState.isLink}
              disabled={!editorState.canSetLink}
            />
          </ToolbarGroup>
          <ToolbarGroup>
            <ToolbarIconButton
              label="Align left"
              active={editorState.isAlignLeft}
              disabled={!editorState.canSetTextAlign}
              onClick={() => editor.chain().focus().setTextAlign('left').run()}
            >
              <Bars3BottomLeftIcon className="size-4" />
            </ToolbarIconButton>
            <ToolbarIconButton
              label="Align center"
              active={editorState.isAlignCenter}
              disabled={!editorState.canSetTextAlign}
              onClick={() => editor.chain().focus().setTextAlign('center').run()}
            >
              <Bars3CenterIcon className="size-4" />
            </ToolbarIconButton>
            <ToolbarIconButton
              label="Align right"
              active={editorState.isAlignRight}
              disabled={!editorState.canSetTextAlign}
              onClick={() => editor.chain().focus().setTextAlign('right').run()}
            >
              <Bars3BottomRightIcon className="size-4" />
            </ToolbarIconButton>
          </ToolbarGroup>
          <ToolbarGroup>
            <ToolbarIconButton
              label="Paragraph"
              active={editorState.isParagraph}
              onClick={() => editor.chain().focus().setParagraph().run()}
            >
              P
            </ToolbarIconButton>
            <ToolbarIconButton
              label="Heading 1"
              active={editorState.isHeading1}
              onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            >
              H1
            </ToolbarIconButton>
            <ToolbarIconButton
              label="Heading 2"
              active={editorState.isHeading2}
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            >
              H2
            </ToolbarIconButton>
            <ToolbarIconButton
              label="Heading 3"
              active={editorState.isHeading3}
              onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            >
              H3
            </ToolbarIconButton>
          </ToolbarGroup>
          <ToolbarGroup>
            <ToolbarIconButton
              label="Bullet list"
              active={editorState.isBulletList}
              onClick={() => editor.chain().focus().toggleBulletList().run()}
            >
              <ListBulletIcon className="size-4" />
            </ToolbarIconButton>
            <ToolbarIconButton
              label="Numbered list"
              active={editorState.isOrderedList}
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
            >
              <NumberedListIcon className="size-4" />
            </ToolbarIconButton>
            <TableMenu
              disabled={!editorState.isInTable}
              actions={[
                {
                  label: 'Insert row before',
                  enabled: editorState.canAddRowBefore,
                  run: () => editor.chain().focus().addRowBeforeLikeSelected().run()
                },
                {
                  label: 'Insert row after',
                  enabled: editorState.canAddRowAfter,
                  run: () => editor.chain().focus().addRowAfterLikeSelected().run()
                },
                {
                  label: 'Insert column before',
                  enabled: editorState.canAddColumnBefore,
                  run: () => editor.chain().focus().addColumnBefore().run()
                },
                {
                  label: 'Insert column after',
                  enabled: editorState.canAddColumnAfter,
                  run: () => editor.chain().focus().addColumnAfter().run()
                },
                {
                  label: 'Delete row',
                  enabled: editorState.canDeleteRow,
                  run: () => editor.chain().focus().deleteRow().run()
                },
                {
                  label: 'Delete column',
                  enabled: editorState.canDeleteColumn,
                  run: () => editor.chain().focus().deleteColumn().run()
                }
              ]}
            />
          </ToolbarGroup>
          {showImageButton && (
            <ToolbarGroup>
              <Headless.Button
                onClick={() => editor.chain().focus().insertImagePlaceholder('').run()}
                disabled={!editorState.canInsertImagePlaceholder}
                className={clsx(
                  'px-2 py-1 text-xs rounded-md whitespace-nowrap flex items-center gap-1',
                  editorState.canInsertImagePlaceholder
                    ? 'hover:bg-neutral-200 dark:hover:bg-white/10 text-neutral-700 dark:text-neutral-300'
                    : 'text-neutral-400 dark:text-neutral-500 cursor-not-allowed'
                )}
                title="Insert image placeholder"
              >
                <PhotoIcon className="size-4" />
                <span>Image</span>
              </Headless.Button>
            </ToolbarGroup>
          )}
          <ToolbarGroup>
            <Headless.Button
              onClick={() => editor.chain().focus().undo().run()}
              disabled={!editorState.canUndo}
              aria-label="Undo"
              title="Undo"
              className="p-1 hover:bg-neutral-200 dark:hover:bg-white/10 rounded-md text-neutral-500 dark:text-neutral-400"
            >
              <ArrowUturnLeftIcon className="size-4" />
            </Headless.Button>
            <Headless.Button
              onClick={() => editor.chain().focus().redo().run()}
              disabled={!editorState.canRedo}
              aria-label="Redo"
              title="Redo"
              className="p-1 hover:bg-neutral-200 dark:hover:bg-white/10 rounded-md text-neutral-500 dark:text-neutral-400"
            >
              <ArrowUturnRightIcon className="size-4" />
            </Headless.Button>
          </ToolbarGroup>
        </div>
        {onPreviewToggle && (
          <Headless.Button
            onClick={onPreviewToggle}
            data-testid="back-to-pages"
            className={clsx(
              'flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium whitespace-nowrap hover:bg-neutral-200 dark:hover:bg-white/10',
              showPreview
                ? 'text-primary-500 dark:text-primary-400'
                : 'text-primary-600 dark:text-primary-400'
            )}
          >
            <DocumentDuplicateIcon className="size-4" />
            Back to pages
          </Headless.Button>
        )}
      </div>
    </div>
  );
};
